import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  BanknotesIcon,
  CalendarIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import DateQuickPresets from '../components/DateQuickPresets';

const buildInitialFiltros = () => ({
  data_inicio: format(new Date().setMonth(new Date().getMonth() - 1), 'yyyy-MM-dd'),
  data_fim: format(new Date(), 'yyyy-MM-dd'),
  descricao: '',
  tipo: 'todos',
  plano_conta_id: 'todos',
  centro_custo_id: 'todos',
  conta_bancaria_id: 'todas',
});

const buildEmptyFiltros = () => ({
  data_inicio: '',
  data_fim: '',
  descricao: '',
  tipo: 'todos',
  plano_conta_id: 'todos',
  centro_custo_id: 'todos',
  conta_bancaria_id: 'todas',
});

const buildInitialBatchForm = () => ({
  apply_conta_bancaria: false,
  conta_bancaria_id: '',
  apply_plano_conta: false,
  plano_conta_id: '',
  apply_centro_custo: false,
  centro_custo_id: '',
});

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const LancamentosEdicaoLote = () => {
  const navigate = useNavigate();
  const [lancamentos, setLancamentos] = useState([]);
  const [planosContas, setPlanosContas] = useState([]);
  const [centrosCustos, setCentrosCustos] = useState([]);
  const [contasBancarias, setContasBancarias] = useState([]);
  const [filtros, setFiltros] = useState(buildInitialFiltros);
  const [mostrarFiltros, setMostrarFiltros] = useState(true);
  const [batchForm, setBatchForm] = useState(buildInitialBatchForm);
  const [selecionados, setSelecionados] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [resultadoLote, setResultadoLote] = useState(null);

  const carregarDropdowns = async () => {
    try {
      const [planosResponse, centrosResponse, contasResponse] = await Promise.all([
        axios.get('/api/plano-contas'),
        axios.get('/api/centro-custos'),
        axios.get('/api/contas-bancarias'),
      ]);

      setPlanosContas(Array.isArray(planosResponse.data) ? planosResponse.data : []);
      setCentrosCustos(Array.isArray(centrosResponse.data) ? centrosResponse.data : []);
      setContasBancarias(Array.isArray(contasResponse.data) ? contasResponse.data : []);
    } catch (error) {
      console.error('Erro ao carregar dados auxiliares da edicao em lote:', error);
      toast.error('Erro ao carregar dados auxiliares');
    }
  };

  const carregarDados = async () => {
    setCarregando(true);
    try {
      const response = await axios.get('/api/lancamentos', { params: filtros });
      const data = Array.isArray(response.data) ? response.data : [];
      setLancamentos(data.map((item) => ({ ...item, valor: toNumber(item?.valor) })));
      setSelecionados((prev) => {
        const idsValidos = new Set(data.map((item) => item.id));
        return prev.filter((id) => idsValidos.has(id));
      });
    } catch (error) {
      console.error('Erro ao carregar lancamentos para edicao em lote:', error);
      toast.error('Erro ao carregar lancamentos');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregarDropdowns();
    carregarDados();
  }, []);

  const idsVisiveis = lancamentos.map((lancamento) => lancamento.id);
  const todosSelecionados = idsVisiveis.length > 0 && idsVisiveis.every((id) => selecionados.includes(id));
  const selecionadosSet = new Set(selecionados);
  const lancamentosSelecionados = lancamentos.filter((item) => selecionadosSet.has(item.id));
  const totalComRateio = lancamentosSelecionados.filter(
    (item) => Array.isArray(item.rateios_centro_custo) && item.rateios_centro_custo.length > 0
  ).length;
  const totalTransferencias = lancamentosSelecionados.filter((item) => item.tipo === 'transferencia').length;
  const totalAlteracoes = [
    batchForm.apply_conta_bancaria,
    batchForm.apply_plano_conta,
    batchForm.apply_centro_custo,
  ].filter(Boolean).length;

  const toggleSelecionado = (id) => {
    setSelecionados((prev) => (
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    ));
  };

  const toggleSelecionarTodos = () => {
    if (todosSelecionados) {
      setSelecionados((prev) => prev.filter((id) => !idsVisiveis.includes(id)));
      return;
    }

    setSelecionados((prev) => Array.from(new Set([...prev, ...idsVisiveis])));
  };

  const atualizarBatchForm = (field, value) => {
    setBatchForm((prev) => ({ ...prev, [field]: value }));
  };

  const aplicarEdicaoEmLote = async () => {
    if (selecionados.length === 0) {
      toast.error('Selecione ao menos 1 lancamento');
      return;
    }

    if (totalAlteracoes === 0) {
      toast.error('Escolha ao menos 1 campo para atualizar');
      return;
    }

    if (batchForm.apply_conta_bancaria && !batchForm.conta_bancaria_id) {
      toast.error('Selecione a conta bancaria');
      return;
    }

    if (batchForm.apply_plano_conta && !batchForm.plano_conta_id) {
      toast.error('Selecione o plano de contas');
      return;
    }

    if (batchForm.apply_centro_custo && !batchForm.centro_custo_id) {
      toast.error('Selecione o centro de custo');
      return;
    }

    if (!window.confirm(`Aplicar a edicao em lote em ${selecionados.length} lancamento(s)?`)) {
      return;
    }

    try {
      setSalvando(true);
      const response = await axios.patch('/api/lancamentos/lote', {
        lancamento_ids: selecionados,
        ...batchForm,
      });
      const data = response.data || {};
      const atualizadosIds = Array.isArray(data.atualizados_ids) ? data.atualizados_ids : [];
      const avisos = Array.isArray(data.avisos) ? data.avisos.length : 0;
      const ignorados = Array.isArray(data.ignorados) ? data.ignorados.length : 0;

      setResultadoLote(data);
      setSelecionados((prev) => prev.filter((id) => !atualizadosIds.includes(id)));

      if (toNumber(data.atualizados) > 0) {
        toast.success(`${data.atualizados} lancamento(s) atualizado(s)`);
      } else {
        toast('Nenhum lancamento foi alterado');
      }

      if (avisos > 0) toast(`${avisos} item(ns) atualizado(s) com restricoes`);
      if (ignorados > 0) toast(`${ignorados} item(ns) ignorado(s)`);

      await carregarDados();
    } catch (error) {
      console.error('Erro ao aplicar edicao em lote:', error);
      toast.error(error.response?.data?.error || 'Erro ao aplicar edicao em lote');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={() => navigate('/lancamentos')}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 transition hover:border-gray-300 hover:text-gray-700"
            title="Voltar para lancamentos"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Edicao em Lote de Lancamentos</h1>
            <p className="text-gray-600">Filtre, selecione as linhas e altere apenas conta bancaria, plano de contas e centro de custo.</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button type="button" onClick={() => setMostrarFiltros((prev) => !prev)} className="btn-secondary">
            <FunnelIcon className="mr-2 h-5 w-5" />
            Filtros
          </button>
          <button type="button" onClick={carregarDados} className="btn-secondary">
            <MagnifyingGlassIcon className="mr-2 h-5 w-5" />
            Atualizar Lista
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-primary-100 bg-primary-50 px-4 py-4 text-sm text-primary-900">
        Conta bancaria altera a conta de origem. Plano de contas e centro de custo nao se aplicam a transferencias.
        Lancamentos com rateio nao recebem centro de custo direto.
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
        <div className="card"><div className="card-body"><div className="flex items-center"><div className="rounded-full bg-primary-100 p-3"><DocumentTextIcon className="h-6 w-6 text-primary-600" /></div><div className="ml-4"><p className="text-sm font-medium text-gray-600">Encontrados</p><p className="text-2xl font-bold text-gray-900">{lancamentos.length}</p></div></div></div></div>
        <div className="card"><div className="card-body"><div className="flex items-center"><div className="rounded-full bg-success-100 p-3"><CheckCircleIcon className="h-6 w-6 text-success-600" /></div><div className="ml-4"><p className="text-sm font-medium text-gray-600">Selecionados</p><p className="text-2xl font-bold text-gray-900">{lancamentosSelecionados.length}</p></div></div></div></div>
        <div className="card"><div className="card-body"><div className="flex items-center"><div className="rounded-full bg-warning-100 p-3"><ExclamationTriangleIcon className="h-6 w-6 text-warning-700" /></div><div className="ml-4"><p className="text-sm font-medium text-gray-600">Transferencias</p><p className="text-2xl font-bold text-gray-900">{totalTransferencias}</p></div></div></div></div>
        <div className="card"><div className="card-body"><div className="flex items-center"><div className="rounded-full bg-danger-100 p-3"><ExclamationTriangleIcon className="h-6 w-6 text-danger-600" /></div><div className="ml-4"><p className="text-sm font-medium text-gray-600">Com Rateio</p><p className="text-2xl font-bold text-gray-900">{totalComRateio}</p></div></div></div></div>
      </div>

      {mostrarFiltros && (
        <div className="card animate-slide-in">
          <div className="card-body p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-base font-semibold">Filtros</h3>
              <button type="button" onClick={() => setMostrarFiltros(false)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>

            <DateQuickPresets
              mode="range"
              startDate={filtros.data_inicio}
              endDate={filtros.data_fim}
              onSelect={({ startDate, endDate }) => setFiltros((old) => ({ ...old, data_inicio: startDate, data_fim: endDate }))}
              className="mb-3"
            />

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
              <div className="form-group mb-0">
                <label className="form-label text-xs mb-1"><CalendarIcon className="mr-1 inline h-3.5 w-3.5" />Data Inicio</label>
                <input type="date" value={filtros.data_inicio} onChange={(e) => setFiltros((old) => ({ ...old, data_inicio: e.target.value }))} className="form-input text-sm py-1.5" />
              </div>
              <div className="form-group mb-0">
                <label className="form-label text-xs mb-1"><CalendarIcon className="mr-1 inline h-3.5 w-3.5" />Data Fim</label>
                <input type="date" value={filtros.data_fim} onChange={(e) => setFiltros((old) => ({ ...old, data_fim: e.target.value }))} className="form-input text-sm py-1.5" />
              </div>
              <div className="form-group mb-0">
                <label className="form-label text-xs mb-1">Descricao</label>
                <input type="text" value={filtros.descricao} onChange={(e) => setFiltros((old) => ({ ...old, descricao: e.target.value }))} className="form-input text-sm py-1.5" placeholder="Buscar por descricao..." />
              </div>
              <div className="form-group mb-0">
                <label className="form-label text-xs mb-1">Tipo</label>
                <select value={filtros.tipo} onChange={(e) => setFiltros((old) => ({ ...old, tipo: e.target.value }))} className="form-select text-sm py-1.5">
                  <option value="todos">Todos</option>
                  <option value="receita">Receitas</option>
                  <option value="despesa">Despesas</option>
                  <option value="transferencia">Transferencias</option>
                </select>
              </div>
              <div className="form-group mb-0">
                <label className="form-label text-xs mb-1">Plano de Contas</label>
                <select value={filtros.plano_conta_id} onChange={(e) => setFiltros((old) => ({ ...old, plano_conta_id: e.target.value }))} className="form-select text-sm py-1.5">
                  <option value="todos">Todos</option>
                  {planosContas.map((plano) => <option key={plano.id} value={plano.id}>{plano.codigo} - {plano.descricao}</option>)}
                </select>
              </div>
              <div className="form-group mb-0">
                <label className="form-label text-xs mb-1">Centro de Custo</label>
                <select value={filtros.centro_custo_id} onChange={(e) => setFiltros((old) => ({ ...old, centro_custo_id: e.target.value }))} className="form-select text-sm py-1.5">
                  <option value="todos">Todos</option>
                  {centrosCustos.map((centro) => <option key={centro.id} value={centro.id}>{centro.codigo} - {centro.descricao}</option>)}
                </select>
              </div>
              <div className="form-group mb-0">
                <label className="form-label text-xs mb-1">Conta Bancaria</label>
                <select value={filtros.conta_bancaria_id} onChange={(e) => setFiltros((old) => ({ ...old, conta_bancaria_id: e.target.value }))} className="form-select text-sm py-1.5">
                  <option value="todas">Todas</option>
                  {contasBancarias.map((conta) => <option key={conta.id} value={conta.id}>{conta.banco} - {conta.conta}</option>)}
                </select>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap justify-between gap-2">
              <button type="button" onClick={() => setFiltros(buildEmptyFiltros())} className="btn-secondary text-sm py-1.5 px-3">Limpar Filtros</button>
              <button type="button" onClick={carregarDados} className="btn-primary text-sm py-1.5 px-3">
                <MagnifyingGlassIcon className="mr-2 h-4 w-4" />
                Aplicar Filtros
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <h3 className="text-lg font-semibold">Campos da edicao em lote</h3>
            <p className="mt-1 text-sm text-gray-500">Ative apenas o que deve mudar nos lancamentos selecionados.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setSelecionados([])} className="btn-secondary" disabled={selecionados.length === 0}>Limpar Selecao</button>
            <button type="button" onClick={() => setBatchForm(buildInitialBatchForm())} className="btn-secondary">Limpar Alteracoes</button>
            <button type="button" onClick={aplicarEdicaoEmLote} className="btn-primary" disabled={salvando || selecionados.length === 0 || totalAlteracoes === 0}>Aplicar Edicao em Lote</button>
          </div>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <div className="rounded-lg border border-gray-200 p-4">
              <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-800">
                <input type="checkbox" checked={batchForm.apply_conta_bancaria} onChange={(e) => atualizarBatchForm('apply_conta_bancaria', e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                Alterar conta bancaria
              </label>
              <select value={batchForm.conta_bancaria_id} onChange={(e) => atualizarBatchForm('conta_bancaria_id', e.target.value)} disabled={!batchForm.apply_conta_bancaria} className="form-select mt-3">
                <option value="">Selecione...</option>
                {contasBancarias.map((conta) => <option key={conta.id} value={conta.id}>{conta.banco} - {conta.conta}</option>)}
              </select>
            </div>
            <div className="rounded-lg border border-gray-200 p-4">
              <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-800">
                <input type="checkbox" checked={batchForm.apply_plano_conta} onChange={(e) => atualizarBatchForm('apply_plano_conta', e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                Alterar plano de contas
              </label>
              <select value={batchForm.plano_conta_id} onChange={(e) => atualizarBatchForm('plano_conta_id', e.target.value)} disabled={!batchForm.apply_plano_conta} className="form-select mt-3">
                <option value="">Selecione...</option>
                {planosContas.map((plano) => <option key={plano.id} value={plano.id}>{plano.codigo} - {plano.descricao}</option>)}
              </select>
            </div>
            <div className="rounded-lg border border-gray-200 p-4">
              <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-800">
                <input type="checkbox" checked={batchForm.apply_centro_custo} onChange={(e) => atualizarBatchForm('apply_centro_custo', e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                Alterar centro de custo
              </label>
              <select value={batchForm.centro_custo_id} onChange={(e) => atualizarBatchForm('centro_custo_id', e.target.value)} disabled={!batchForm.apply_centro_custo} className="form-select mt-3">
                <option value="">Selecione...</option>
                {centrosCustos.map((centro) => <option key={centro.id} value={centro.id}>{centro.codigo} - {centro.descricao}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      {resultadoLote && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-base font-semibold">Resumo da ultima execucao</h3>
          </div>
          <div className="card-body grid grid-cols-1 gap-4 xl:grid-cols-3">
            <div className="rounded-lg border border-gray-200 p-4 text-sm text-gray-700">
              <p><span className="font-semibold text-gray-900">Solicitados:</span> {toNumber(resultadoLote.total_solicitado)}</p>
              <p><span className="font-semibold text-gray-900">Atualizados:</span> {toNumber(resultadoLote.atualizados)}</p>
            </div>
            <div className="rounded-lg border border-gray-200 p-4 text-sm text-gray-700">
              <p className="font-semibold text-gray-900">Avisos</p>
              <p>{Array.isArray(resultadoLote.avisos) ? resultadoLote.avisos.length : 0} item(ns)</p>
            </div>
            <div className="rounded-lg border border-gray-200 p-4 text-sm text-gray-700">
              <p className="font-semibold text-gray-900">Ignorados</p>
              <p>{Array.isArray(resultadoLote.ignorados) ? resultadoLote.ignorados.length : 0} item(ns)</p>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h3 className="text-lg font-semibold">Lancamentos filtrados ({lancamentos.length})</h3>
          {carregando && <div className="loader h-6 w-6" />}
        </div>
        <div className="card-body p-0">
          {lancamentos.length > 0 ? (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th className="w-16">
                      <input type="checkbox" checked={todosSelecionados} onChange={toggleSelecionarTodos} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                    </th>
                    <th>Tipo</th>
                    <th>Data</th>
                    <th>Descricao</th>
                    <th>Detalhes</th>
                    <th>Valor</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {lancamentos.map((lancamento) => {
                    const possuiRateio = Array.isArray(lancamento.rateios_centro_custo) && lancamento.rateios_centro_custo.length > 0;
                    return (
                      <tr key={lancamento.id}>
                        <td>
                          <input type="checkbox" checked={selecionados.includes(lancamento.id)} onChange={() => toggleSelecionado(lancamento.id)} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                        </td>
                        <td>
                          <div className="flex flex-wrap gap-2">
                            <span className={lancamento.tipo === 'receita' ? 'badge-success' : lancamento.tipo === 'despesa' ? 'badge-danger' : 'badge-info'}>
                              {lancamento.tipo === 'receita' ? 'Receita' : lancamento.tipo === 'despesa' ? 'Despesa' : 'Transferencia'}
                            </span>
                            {possuiRateio && <span className="badge-warning">Rateio</span>}
                          </div>
                        </td>
                        <td>{format(parseISO(lancamento.data), 'dd/MM/yyyy')}</td>
                        <td>
                          <div className="max-w-xs">
                            <p className="font-medium text-gray-900">{lancamento.descricao}</p>
                            <p className="text-xs text-gray-500">ID: {lancamento.id}</p>
                          </div>
                        </td>
                        <td>
                          <div className="max-w-sm text-sm">
                            <p className="font-medium text-gray-900">{lancamento.plano_conta_descricao || 'Sem plano de conta'}</p>
                            <p className="text-xs text-gray-500">
                              {lancamento.tipo === 'transferencia'
                                ? `Destino: ${lancamento.conta_destino_banco || lancamento.conta_destino_id || 'Sem destino'}`
                                : (possuiRateio ? `Rateado em ${lancamento.rateios_centro_custo.length} centro(s)` : (lancamento.centro_custo_descricao || 'Sem centro de custo'))}
                            </p>
                            <p className="text-xs text-gray-500">Conta: {lancamento.conta_bancaria_banco || 'Sem conta'}</p>
                          </div>
                        </td>
                        <td>
                          <span className={lancamento.tipo === 'receita' ? 'font-semibold text-success-600' : lancamento.tipo === 'despesa' ? 'font-semibold text-danger-600' : 'font-semibold text-primary-600'}>
                            R$ {lancamento.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </td>
                        <td>
                          <span className={lancamento.status === 'confirmado' ? 'badge-success' : lancamento.status === 'pendente' ? 'badge-warning' : 'badge-danger'}>
                            {lancamento.status === 'confirmado' ? 'Confirmado' : lancamento.status === 'pendente' ? 'Pendente' : 'Cancelado'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <BanknotesIcon className="mx-auto mb-3 h-12 w-12 text-gray-300" />
              <p className="text-lg font-medium">Nenhum lancamento encontrado</p>
              <p className="mt-1 text-sm">Ajuste os filtros para localizar os itens da edicao em lote.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LancamentosEdicaoLote;
